import React from "react";
import { useSummaryCardIsEditable } from "../summary-card/summary-card-editable-context";
import "./labeled-value.scss";

export type LabeledValueEditAs = "text" | "number" | "date" | "select";

export interface LabeledValueEditOption {
  value: string;
  label: string;
}

export interface LabeledValueProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  label: string;
  value?: string | React.ReactNode;
  labelColor?: string;
  valueColor?: string;
  labelIcon?: React.ReactNode;
  labelIconTooltip?: string;
  valueIconLeft?: React.ReactNode;
  valueIconLeftTooltip?: string;
  valueIconRight?: React.ReactNode;
  valueIconRightTooltip?: string;
  labelClassName?: string;
  valueClassName?: string;
  direction?: "vertical" | "horizontal";
  gap?: string | number;
  truncate?: boolean;
  className?: string;
  /**
   * When true, the value is shown with an editable control instead of plain text.
   * When omitted, inherits `SummaryCard`'s `isEditable` when rendered inside that card.
   */
  isEditable?: boolean;
  /**
   * Native control when `isEditable` is true. Defaults to `"text"` (string `value` required
   * except for `select`, where `editOptions` is enough and `value` may be `""` for empty).
   */
  editAs?: LabeledValueEditAs;
  /** Options for `editAs="select"` (required when editing as select). */
  editOptions?: LabeledValueEditOption[];
  /** Fired when the user edits the value in editable mode. */
  onValueChange?: (next: string) => void;
  /**
   * When true in editable mode, shows a red asterisk on the label and a red border on the
   * input or select to indicate the field is required.
   */
  required?: boolean;
  /** Passed through to the editable `<input>` for `text` | `number` | `date`. */
  inputProps?: Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "value" | "onChange" | "type"
  >;
  /** Passed through to the editable `<select>` when `editAs` is `"select"`. */
  selectProps?: Omit<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    "value" | "onChange"
  >;
}

const LabeledValue = ({
  label,
  value,
  labelColor,
  valueColor,
  labelIcon,
  labelIconTooltip,
  valueIconLeft,
  valueIconLeftTooltip,
  valueIconRight,
  valueIconRightTooltip,
  labelClassName,
  valueClassName,
  direction = "vertical",
  gap,
  truncate = false,
  className,
  style,
  isEditable: isEditableProp,
  editAs,
  editOptions,
  onValueChange,
  required = false,
  inputProps,
  selectProps,
  ...rest
}: LabeledValueProps) => {
  const cls = "labeled-value";
  const inheritedEditable = useSummaryCardIsEditable();
  const isEditable =
    isEditableProp !== undefined ? isEditableProp : inheritedEditable;
  const effectiveEditAs: LabeledValueEditAs = editAs ?? "text";

  const stringValue = typeof value === "string" ? value : undefined;
  const selectEditReady =
    isEditable &&
    effectiveEditAs === "select" &&
    Array.isArray(editOptions) &&
    editOptions.length > 0;
  const scalarEditReady =
    isEditable &&
    effectiveEditAs !== "select" &&
    stringValue !== undefined;
  const showEditControl = Boolean(selectEditReady || scalarEditReady);

  const [uncontrolledDraft, setUncontrolledDraft] = React.useState(() =>
    effectiveEditAs === "select"
      ? (typeof value === "string" ? value : "")
      : (stringValue ?? ""),
  );
  React.useEffect(() => {
    if (effectiveEditAs === "select") {
      setUncontrolledDraft(typeof value === "string" ? value : "");
    } else if (typeof value === "string") {
      setUncontrolledDraft(value);
    }
  }, [value, effectiveEditAs]);

  const readOnlyDisplay: React.ReactNode =
    !isEditable &&
    effectiveEditAs === "select" &&
    editOptions &&
    editOptions.length > 0 &&
    typeof value === "string"
      ? (editOptions.find((o) => o.value === value)?.label ?? value)
      : value;

  const showRequiredState = required && showEditControl;

  const rootClass = [
    cls,
    direction === "horizontal" ? `${cls}--horizontal` : "",
    truncate ? `${cls}--truncate` : "",
    showEditControl ? `${cls}--with-input` : "",
    showRequiredState ? `${cls}--required` : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const rootStyle: React.CSSProperties = {
    ...(gap != null
      ? { gap: typeof gap === "number" ? `${gap}px` : gap }
      : {}),
    ...style,
    // Label must sit flush above the input’s left edge (overrides e.g. alignItems: flex-end for read-only)
    ...(showEditControl
      ? { alignItems: "flex-start", textAlign: "left" as const }
      : {}),
  };

  const hasStyle = Object.keys(rootStyle).length > 0;

  const hasLabelIcon = labelIcon != null;
  const hasValueIcons = valueIconLeft != null || valueIconRight != null;

  const inputClassName = [
    `${cls}__input`,
    showRequiredState ? `${cls}__input--required` : "",
    inputProps?.className,
  ]
    .filter(Boolean)
    .join(" ");
  const selectClassName = [
    `${cls}__select`,
    showRequiredState ? `${cls}__select--required` : "",
    selectProps?.className,
  ]
    .filter(Boolean)
    .join(" ");

  const requiredIndicator = showRequiredState ? (
    <span className={`${cls}__required`} aria-hidden="true">
      {" "}
      *
    </span>
  ) : null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (onValueChange) {
      onValueChange(next);
    } else {
      setUncontrolledDraft(next);
    }
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value;
    if (onValueChange) {
      onValueChange(next);
    } else {
      setUncontrolledDraft(next);
    }
  };

  const scalarFieldValue = onValueChange
    ? (stringValue ?? "")
    : uncontrolledDraft;

  const selectFieldValue = onValueChange
    ? typeof value === "string"
      ? value
      : ""
    : uncontrolledDraft;

  const inputType: React.HTMLInputTypeAttribute =
    effectiveEditAs === "number"
      ? "number"
      : effectiveEditAs === "date"
        ? "date"
        : "text";

  const editControlEl = showEditControl
    ? effectiveEditAs === "select" && editOptions
      ? (
          <select
            {...selectProps}
            className={selectClassName}
            value={selectFieldValue}
            onChange={handleSelectChange}
            required={showRequiredState || undefined}
            aria-required={showRequiredState || undefined}
          >
            {editOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )
      : (
          <input
            {...inputProps}
            type={inputType}
            className={inputClassName}
            value={scalarFieldValue}
            onChange={handleInputChange}
            required={showRequiredState || undefined}
            aria-required={showRequiredState || undefined}
          />
        )
    : null;

  return (
    <div
      className={rootClass}
      style={hasStyle ? rootStyle : undefined}
      {...rest}
    >
      {hasLabelIcon ? (
        <span
          className={[`${cls}__label-row`, labelClassName]
            .filter(Boolean)
            .join(" ")}
          style={labelColor ? { color: labelColor } : undefined}
        >
          <span
            className={`${cls}__label`}
            title={truncate ? label : undefined}
          >
            {label}
            {requiredIndicator}
          </span>
          <span
            className={[
              `${cls}__icon`,
              labelIconTooltip ? `${cls}__icon--has-tooltip` : "",
            ]
              .filter(Boolean)
              .join(" ")}
            title={labelIconTooltip}
          >
            {labelIcon}
          </span>
        </span>
      ) : (
        <span
          className={[`${cls}__label`, labelClassName]
            .filter(Boolean)
            .join(" ")}
          style={labelColor ? { color: labelColor } : undefined}
          title={truncate ? label : undefined}
        >
          {label}
          {requiredIndicator}
        </span>
      )}

      {(readOnlyDisplay != null || showEditControl) &&
        (showEditControl ? (
          hasValueIcons ? (
            <span
              className={[`${cls}__value-row`, valueClassName]
                .filter(Boolean)
                .join(" ")}
            >
              {valueIconLeft != null && (
                <span
                  className={[
                    `${cls}__icon`,
                    valueIconLeftTooltip ? `${cls}__icon--has-tooltip` : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  title={valueIconLeftTooltip}
                >
                  {valueIconLeft}
                </span>
              )}
              {editControlEl}
              {valueIconRight != null && (
                <span
                  className={[
                    `${cls}__icon`,
                    valueIconRightTooltip ? `${cls}__icon--has-tooltip` : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  title={valueIconRightTooltip}
                >
                  {valueIconRight}
                </span>
              )}
            </span>
          ) : (
            editControlEl
          )
        ) : hasValueIcons ? (
          <span
            className={[`${cls}__value-row`, valueClassName]
              .filter(Boolean)
              .join(" ")}
            style={valueColor ? { color: valueColor } : undefined}
          >
            {valueIconLeft != null && (
              <span
                className={[
                  `${cls}__icon`,
                  valueIconLeftTooltip ? `${cls}__icon--has-tooltip` : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                title={valueIconLeftTooltip}
              >
                {valueIconLeft}
              </span>
            )}
            <span
              className={`${cls}__value`}
              title={
                truncate && typeof readOnlyDisplay === "string"
                  ? readOnlyDisplay
                  : undefined
              }
            >
              {readOnlyDisplay}
            </span>
            {valueIconRight != null && (
              <span
                className={[
                  `${cls}__icon`,
                  valueIconRightTooltip ? `${cls}__icon--has-tooltip` : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                title={valueIconRightTooltip}
              >
                {valueIconRight}
              </span>
            )}
          </span>
        ) : (
          <span
            className={[`${cls}__value`, valueClassName]
              .filter(Boolean)
              .join(" ")}
            style={valueColor ? { color: valueColor } : undefined}
            title={
              truncate && typeof readOnlyDisplay === "string"
                ? readOnlyDisplay
                : undefined
            }
          >
            {readOnlyDisplay}
          </span>
        ))}
    </div>
  );
};

export { LabeledValue };
export default LabeledValue;
